import { useCallback, useEffect, useState } from 'react';

/**
 * A router in forty lines.
 *
 * # Why not react-router
 *
 * This application has six routes and one dynamic segment. A routing library
 * would add a dependency tree, a nested-route API and a data-loading model to
 * solve a problem that is `switch (path)`. The Phase 7C brief §34 asks for a
 * simple SPA and for libraries with a real purpose; this is the shape that
 * request has.
 *
 * The History API is used directly, so deep links, the back button and a
 * refresh all work — which is what the Netlify SPA fallback exists to support.
 */

export type Route =
  | { name: 'dashboard' }
  | { name: 'installations' }
  | { name: 'installation'; id: string }
  | { name: 'activate'; code?: string }
  | { name: 'plan' }
  | { name: 'settings' }
  // Organisation administration — Phase 17B. Members, teams, service accounts
  // and where people come from. One route with tabs rather than four routes:
  // they are one job, and four entries in a six-item nav would bury the fleet.
  //
  // Phase 21: its sections are paths (/organization/teams, …) so each is a
  // link that can be shared and the back button works. `/organization` is the
  // People section, so every bookmark made before Phase 21 still lands.
  | { name: 'organization'; section: OrganizationSection }
  // Accepting an invitation. The token is in the FRAGMENT of the link, which
  // a browser never sends to a server; the page reads it from there.
  | { name: 'invite' }
  // Where the billing provider sends the customer back. `complete` is the
  // success return and `/billing` the cancel return — both are the provider's
  // redirect targets, set by the API in handleStartCheckout, so they must exist
  // here or a paid customer lands on "not found" at the worst possible moment.
  // Neither is evidence of payment: the page says what happened and reads state
  // from the API, which learns it from a verified webhook.
  | { name: 'billing'; complete: boolean }
  // The fleet — Phase 14. `/fleet` sits BESIDE `/installations` rather than
  // replacing it (owner decision OD-4): an organisation may own several
  // installations, which are separate fleets that cannot schedule against each
  // other, and merging them into one list would imply capacity that does not
  // exist.
  | { name: 'fleet' }
  | { name: 'fleetMachine'; id: string }
  | { name: 'fleetWorkloads' }
  | { name: 'fleetRun' }
  // Resource governance — Phase 17C. Under /fleet because that is what it
  // governs: a policy is about one fleet, and an organisation with several has
  // several policies.
  | { name: 'fleetGovernance' }
  // Fleet rollouts — Phase 18B. Under /fleet, beside the machines it moves.
  | { name: 'fleetUpgrade' }
  // Usage and audit — Phase 17D. One route rather than two: "what did my
  // hardware do" and "what changed" are read in the same sitting, and two
  // entries in the nav for one question buries the fleet the way four 17B
  // routes would have.
  | { name: 'usage' }
  | { name: 'signin'; error?: string }
  | { name: 'notfound'; path: string };

export type OrganizationSection = 'members' | 'teams' | 'service-accounts' | 'sign-in' | 'settings';

const SECTIONS: OrganizationSection[] = ['members', 'teams', 'service-accounts', 'sign-in', 'settings'];

export function parseRoute(pathname: string, search: string): Route {
  const params = new URLSearchParams(search);
  // Trailing slashes are stripped so /installations and /installations/ are one
  // route rather than two that differ only in whether a list renders.
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') return { name: 'dashboard' };
  if (path === '/installations') return { name: 'installations' };
  if (path.startsWith('/installations/')) {
    const id = decodeURIComponent(path.slice('/installations/'.length));
    return id ? { name: 'installation', id } : { name: 'installations' };
  }
  if (path === '/activate') return { name: 'activate', code: params.get('code') ?? undefined };
  if (path === '/plan') return { name: 'plan' };
  if (path === '/settings') return { name: 'settings' };
  if (path === '/organization') return { name: 'organization', section: 'members' };
  if (path.startsWith('/organization/')) {
    const section = path.slice('/organization/'.length) as OrganizationSection;
    return SECTIONS.includes(section)
      ? { name: 'organization', section }
      : { name: 'organization', section: 'members' };
  }
  if (path === '/invite') return { name: 'invite' };
  if (path === '/billing') return { name: 'billing', complete: false };
  if (path === '/billing/complete') return { name: 'billing', complete: true };
  if (path === '/fleet') return { name: 'fleet' };
  if (path === '/fleet/workloads') return { name: 'fleetWorkloads' };
  if (path === '/fleet/run') return { name: 'fleetRun' };
  if (path === '/fleet/governance') return { name: 'fleetGovernance' };
  if (path === '/fleet/upgrade') return { name: 'fleetUpgrade' };
  if (path === '/usage') return { name: 'usage' };
  if (path.startsWith('/fleet/machines/')) {
    const id = decodeURIComponent(path.slice('/fleet/machines/'.length));
    return id ? { name: 'fleetMachine', id } : { name: 'fleet' };
  }
  if (path === '/signin') return { name: 'signin', error: params.get('error') ?? undefined };
  return { name: 'notfound', path };
}

export function useRoute(): [Route, (to: string) => void] {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, '', to);
    const url = new URL(to, window.location.origin);
    setRoute(parseRoute(url.pathname, url.search));
  }, []);

  return [route, navigate];
}

/** hrefFor keeps link targets in one place, so a path change is one edit. */
export const hrefFor = {
  dashboard: () => '/',
  installations: () => '/installations',
  installation: (id: string) => `/installations/${encodeURIComponent(id)}`,
  activate: (code?: string) => (code ? `/activate?code=${encodeURIComponent(code)}` : '/activate'),
  plan: () => '/plan',
  settings: () => '/settings',
  organization: (section?: OrganizationSection) =>
    section && section !== 'members' ? `/organization/${section}` : '/organization',
  invite: () => '/invite',
  signin: () => '/signin',
  fleet: () => '/fleet',
  fleetMachine: (id: string) => `/fleet/machines/${encodeURIComponent(id)}`,
  fleetWorkloads: () => '/fleet/workloads',
  fleetRun: () => '/fleet/run',
  fleetGovernance: () => '/fleet/governance',
  fleetUpgrade: () => '/fleet/upgrade',
  usage: () => '/usage',
};
